import { useState, type ReactNode } from 'react';
import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { AIAttribution } from '../AIAttribution';
import { CapabilityGate } from '../CapabilityGate';
import { useAICapability } from '../../hooks/useAICapability';
import { useLeadInsights, useFollowUpDraft, useCallCoach, useParseIntent } from '../../hooks/useAIGenerative';
import { useLeadScores } from '../../hooks/useAIAnalytics';

/** Honest output: text when present, honest-unavailable when the provider returns null. */
function GenerativeOutput({
  isPending,
  isSuccess,
  value,
}: {
  isPending: boolean;
  isSuccess: boolean;
  value: string | null | undefined;
}) {
  if (isPending) return <div className="flex justify-center py-6"><Spinner /></div>;
  if (value) {
    return (
      <div className="rounded-card border border-outline bg-surface p-4">
        <div className="whitespace-pre-wrap text-data-md text-on-surface">{value}</div>
        <AIAttribution kind="generation" />
      </div>
    );
  }
  if (isSuccess && (value === null || value === '')) {
    return (
      <EmptyState
        title="Generative AI is currently unavailable"
        description="This output could not be generated because the AI provider is not connected. It will work once a provider is configured."
      />
    );
  }
  return null;
}

// ─── Lead Insights ───────────────────────────────────────────────────────────
export function LeadInsightsTool() {
  const { state, refetch, isFetching } = useAICapability();
  const { data: leads = [] } = useLeadScores();
  const insights = useLeadInsights();
  const [leadId, setLeadId] = useState('');
  const options = [{ value: '', label: 'Select a lead…' }, ...leads.map((l) => ({ value: l.id, label: l.name }))];

  return (
    <Card>
      <CardHeader title="AI Lead Insights" />
      <CardBody className="space-y-3">
        <CapabilityGate state={state} onRetry={() => refetch()} retrying={isFetching}>
          <div className="flex items-end gap-2">
            <div className="w-72">
              <Select label="Lead" name="ai-lead-insight" value={leadId} onChange={(e) => setLeadId(e.target.value)} options={options} />
            </div>
            <Button disabled={!leadId} loading={insights.isPending} onClick={() => insights.mutate(leadId)}>
              Generate
            </Button>
          </div>
          <GenerativeOutput isPending={insights.isPending} isSuccess={insights.isSuccess} value={insights.data} />
        </CapabilityGate>
      </CardBody>
    </Card>
  );
}

// ─── Follow-up Draft ───────────────────────────────────────────────────────────
export function FollowUpDraftTool() {
  const { state, refetch, isFetching } = useAICapability();
  const draft = useFollowUpDraft();
  const [leadName, setLeadName] = useState('');
  const [niche, setNiche] = useState('');

  return (
    <Card>
      <CardHeader title="AI Follow-up Draft" />
      <CardBody className="space-y-3">
        <CapabilityGate state={state} onRetry={() => refetch()} retrying={isFetching}>
          <div className="grid grid-cols-2 gap-2">
            <Input label="Lead name" name="fu-lead" value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="e.g. The Coffee Lab" />
            <Input label="Industry" name="fu-niche" value={niche} onChange={(e) => setNiche(e.target.value)} placeholder="e.g. Hospitality" />
          </div>
          <Button loading={draft.isPending} onClick={() => draft.mutate({ leadName, niche })}>
            Draft Message
          </Button>
          <GenerativeOutput isPending={draft.isPending} isSuccess={draft.isSuccess} value={draft.data} />
        </CapabilityGate>
      </CardBody>
    </Card>
  );
}

// ─── Call Coach ────────────────────────────────────────────────────────────────
export function CallCoachTool() {
  const { state, refetch, isFetching } = useAICapability();
  const coach = useCallCoach();
  const [leadName, setLeadName] = useState('');
  const [disposition, setDisposition] = useState('talked');
  const [notes, setNotes] = useState('');

  return (
    <Card>
      <CardHeader title="AI Post-Call Coach" />
      <CardBody className="space-y-3">
        <CapabilityGate state={state} onRetry={() => refetch()} retrying={isFetching}>
          <div className="grid grid-cols-2 gap-2">
            <Input label="Lead name" name="cc-lead" value={leadName} onChange={(e) => setLeadName(e.target.value)} placeholder="Lead" />
            <Select
              label="Outcome"
              name="cc-disp"
              value={disposition}
              onChange={(e) => setDisposition(e.target.value)}
              options={[
                { value: 'talked', label: 'Connected' },
                { value: 'no_answer', label: 'No answer' },
                { value: 'busy', label: 'Busy' },
                { value: 'not_reachable', label: 'Not reachable' },
              ]}
            />
          </div>
          <Input label="Call notes" name="cc-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What happened on the call…" />
          <Button loading={coach.isPending} onClick={() => coach.mutate({ leadName, disposition, notes })}>
            Get Coaching
          </Button>
          <GenerativeOutput isPending={coach.isPending} isSuccess={coach.isSuccess} value={coach.data} />
        </CapabilityGate>
      </CardBody>
    </Card>
  );
}

// ─── Intent Parser (natural-language create) ────────────────────────────────────
export function IntentParserTool() {
  const { state, refetch, isFetching } = useAICapability();
  const parse = useParseIntent();
  const [text, setText] = useState('');

  const renderResult = (): ReactNode => {
    if (parse.isPending) return <div className="flex justify-center py-6"><Spinner /></div>;
    if (!parse.data) return null;
    const r = parse.data;
    return (
      <div className="space-y-2 rounded-card border border-outline bg-surface p-4">
        <div className="flex items-center gap-2">
          <Badge tone={r.success ? 'success' : 'warning'}>{r.success ? r.entityType ?? 'parsed' : 'not recognised'}</Badge>
        </div>
        <p className="text-data-md text-on-surface">{r.message}</p>
        {r.success && Object.keys(r.fields).length > 0 && (
          <ul className="list-disc pl-5 text-body-sm text-on-surface-variant">
            {Object.entries(r.fields).map(([k, v]) => (
              <li key={k}><span className="font-medium">{k}:</span> {String(v)}</li>
            ))}
          </ul>
        )}
        <AIAttribution kind="generation" />
      </div>
    );
  };

  return (
    <Card>
      <CardHeader title="AI Quick Create (Intent Parser)" />
      <CardBody className="space-y-3">
        <CapabilityGate state={state} onRetry={() => refetch()} retrying={isFetching}>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Input
                label="Describe what to create"
                name="intent-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="e.g. schedule a meeting with Ali tomorrow 3pm"
              />
            </div>
            <Button disabled={!text.trim()} loading={parse.isPending} onClick={() => parse.mutate(text.trim())}>
              Parse
            </Button>
          </div>
          {renderResult()}
        </CapabilityGate>
      </CardBody>
    </Card>
  );
}
